import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Card, PageHeader, Pill } from "./_shared";
import { Upload, Eye, RotateCcw, Save, Send, Cake, Sparkles, Play } from "lucide-react";
import { toast } from "sonner";

const KINDS = {
  welcome: {
    key: "welcome",
    label: "Boas-Vindas",
    icon: Sparkles,
    version_prefix: "WELCOME_V",
    template_endpoint: "/admin/templates/welcome",
    image_endpoint: "/admin/templates/welcome/image",
    preview_endpoint: "/admin/communications/welcome/preview",
    test_endpoint: "/admin/communications/welcome/test",
    recommend: "1672×941px — a região central-direita é reservada para o nome do cliente.",
  },
  birthday: {
    key: "birthday",
    label: "Aniversário",
    icon: Cake,
    version_prefix: "BIRTHDAY_V",
    template_endpoint: "/admin/templates/birthday",
    image_endpoint: "/admin/templates/birthday/image",
    preview_endpoint: "/admin/communications/birthday/preview",
    test_endpoint: "/admin/communications/birthday/test",
    recommend: "Mesmo formato do cartão de boas-vindas (1672×941px). Se não subir uma imagem própria, o Hermes reaproveita a arte de boas-vindas.",
  },
};

export default function AdminTemplates() {
  const [tab, setTab] = useState("welcome");
  return (
    <div className="p-3 sm:p-6 lg:p-8" data-testid="admin-templates">
      <PageHeader
        title="Templates de comunicação"
        subtitle="Edite imagem, texto e assunto sem tocar em código. Cada salvamento gera uma nova versão auditada."
        right={
          <div className="flex bg-[#0f1524] border border-white/5 rounded-xl p-1" data-testid="tpl-tabs">
            {Object.values(KINDS).map(k => {
              const Icon = k.icon;
              return (
                <button key={k.key} onClick={() => setTab(k.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 ${tab === k.key ? "bg-[#d4a24c] text-black font-semibold" : "text-slate-300 hover:bg-white/5"}`}
                  data-testid={`tpl-tab-${k.key}`}>
                  <Icon className="w-3.5 h-3.5"/> {k.label}
                </button>
              );
            })}
          </div>
        }
      />
      {tab === "welcome" && <TemplateEditor kind={KINDS.welcome} defaultName="Ana Beatriz Ramos" defaultTreat="Dra." />}
      {tab === "birthday" && <TemplateEditor kind={KINDS.birthday} defaultName="João Silva" defaultTreat="Dr." showRunNow />}
    </div>
  );
}

function TemplateEditor({ kind, defaultName, defaultTreat, showRunNow }) {
  const [tpl, setTpl] = useState(null);
  const [meta, setMeta] = useState({ version_id: kind.version_prefix + "1", image_is_custom: false, active_image_kind: "default" });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewSrc, setPreviewSrc] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewName, setPreviewName] = useState(defaultName);
  const [previewTreat, setPreviewTreat] = useState(defaultTreat);
  const [baseSrc, setBaseSrc] = useState("");
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const fileInput = useRef(null);

  const load = async () => {
    const r = await api.get(kind.template_endpoint);
    setTpl(r.data.template);
    setMeta({
      version_id: r.data.version_id,
      image_is_custom: r.data.image_is_custom,
      active_image_kind: r.data.active_image_kind,
      updated_at: r.data.updated_at,
    });
    setDirty(false);
    loadBase();
  };
  const loadBase = async () => {
    try {
      const r = await fetch(`${API}${kind.image_endpoint}?t=${Date.now()}`, { credentials: "include" });
      if (r.ok) setBaseSrc(URL.createObjectURL(await r.blob()));
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind.key]);

  const setField = (k) => (e) => { setTpl({ ...tpl, [k]: e.target.value }); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(kind.template_endpoint, {
        subject: tpl.subject, salutation_format: tpl.salutation_format,
        intro: tpl.intro, body: tpl.body, tagline: tpl.tagline, footer: tpl.footer,
      });
      setMeta(m => ({ ...m, version_id: r.data.version_id }));
      setDirty(false);
      toast.success(`Template salva — nova versão ${r.data.version_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar template");
    } finally { setSaving(false); }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Arquivo acima de 8MB");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(kind.image_endpoint, fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Imagem substituída");
      await loadBase();
      setPreviewSrc("");
      setMeta(m => ({ ...m, image_is_custom: true, active_image_kind: "custom" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao subir imagem");
    } finally { setUploading(false); }
  };

  const resetImage = async () => {
    if (!confirm("Voltar para a imagem original?")) return;
    const r = await api.delete(kind.image_endpoint);
    setMeta(m => ({ ...m, image_is_custom: false, active_image_kind: r.data.active_image_kind || "default" }));
    setPreviewSrc("");
    await loadBase();
    toast.success("Imagem revertida para o padrão");
  };

  const doPreview = async () => {
    setPreviewLoading(true);
    try {
      const p = new URLSearchParams({ name: previewName, treatment: previewTreat });
      const r = await fetch(`${API}${kind.preview_endpoint}?${p}`, { credentials: "include" });
      setPreviewSrc(URL.createObjectURL(await r.blob()));
    } finally { setPreviewLoading(false); }
  };

  const sendTest = async () => {
    const r = await api.post(kind.test_endpoint, { name: previewName, treatment: previewTreat });
    if (r.data?.ok) toast.success("Teste enviado — verifique sua caixa de entrada");
    else toast.error("Falha ao enviar teste");
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/communications/birthday/run-now");
      setLastRun(r.data);
      toast.success(`Cron executado: ${r.data.sent} enviado(s), ${r.data.skipped_already} já feito(s)`);
    } catch (e) {
      toast.error("Falha ao executar cron");
    } finally { setRunning(false); }
  };

  if (!tpl) return <div className="text-slate-400">Carregando…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Pill tone="gold">{meta.version_id}</Pill>
          {meta.active_image_kind === "fallback-welcome" && (
            <Pill tone="amber">Usando imagem de boas-vindas — envie uma arte específica ↓</Pill>
          )}
        </div>
        {showRunNow && (
          <button onClick={runNow} disabled={running}
            className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm flex items-center gap-2 disabled:opacity-40"
            data-testid="tpl-run-now">
            <Play className="w-4 h-4"/> {running ? "Rodando…" : "Rodar cron agora"}
          </button>
        )}
      </div>

      {lastRun && (
        <div className="mb-4 p-3 rounded-lg bg-emerald-900/30 border border-emerald-800/60 text-sm text-emerald-200" data-testid="tpl-run-result">
          Cron do dia <b>{lastRun.date}</b>: enviados <b>{lastRun.sent}</b> · já feitos <b>{lastRun.skipped_already}</b> · sem e-mail <b>{lastRun.skipped_missing_email}</b> · falhas <b>{lastRun.failed}</b>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 space-y-4">
          <Card title="Texto do e-mail" kicker="Editável">
            <div className="space-y-3">
              <FieldTA label="Assunto" value={tpl.subject} onChange={setField("subject")} testid="tpl-subject"/>
              <FieldTA label="Saudação (use {treatment} e {name})" value={tpl.salutation_format} onChange={setField("salutation_format")} testid="tpl-salutation"/>
              <FieldTA label="Introdução" value={tpl.intro} onChange={setField("intro")} testid="tpl-intro"/>
              <FieldTA label="Corpo" value={tpl.body} onChange={setField("body")} rows={5} testid="tpl-body"/>
              <FieldTA label="Slogan" value={tpl.tagline} onChange={setField("tagline")} testid="tpl-tagline"/>
              <FieldTA label="Rodapé" value={tpl.footer} onChange={setField("footer")} testid="tpl-footer"/>
            </div>
            <div className="mt-4 flex justify-end">
              <button disabled={!dirty || saving} onClick={save}
                className="px-4 py-2 rounded-lg bg-[#d4a24c] text-black text-sm font-semibold flex items-center gap-2 disabled:opacity-40"
                data-testid="tpl-save">
                <Save className="w-4 h-4"/> {saving ? "Salvando…" : "Salvar nova versão"}
              </button>
            </div>
          </Card>

          <Card title="Imagem base do cartão"
            kicker={meta.active_image_kind === "custom" ? "Personalizada" : meta.active_image_kind === "fallback-welcome" ? "Padrão (boas-vindas)" : "Original"}
            right={meta.image_is_custom && (
              <button onClick={resetImage} className="text-xs text-slate-300 hover:text-[#d4a24c] flex items-center gap-1" data-testid="tpl-image-reset">
                <RotateCcw className="w-3 h-3"/> Restaurar original
              </button>
            )}
          >
            {baseSrc && (
              <img src={baseSrc} alt="base atual" className="w-full rounded-lg border border-white/10 mb-3" data-testid="tpl-image-current"/>
            )}
            <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={e => uploadImage(e.target.files?.[0])} data-testid="tpl-image-input"/>
            <button onClick={()=>fileInput.current?.click()} disabled={uploading}
              className="w-full px-4 py-3 rounded-lg border-2 border-dashed border-white/10 hover:border-[#d4a24c] text-slate-300 hover:text-[#d4a24c] transition flex items-center justify-center gap-2"
              data-testid="tpl-image-upload">
              <Upload className="w-4 h-4"/> {uploading ? "Enviando…" : "Substituir imagem base (PNG/JPG até 8MB)"}
            </button>
            <p className="text-[11px] text-slate-500 mt-2">{kind.recommend}</p>
          </Card>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <Card title="Prévia personalizada" kicker="Como o cliente verá">
            <div className="grid grid-cols-2 gap-2 mb-3">
              <select value={previewTreat} onChange={e=>setPreviewTreat(e.target.value)}
                className="bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200" data-testid="tpl-preview-treatment">
                <option value="">— sem —</option>
                <option value="Dr.">Dr.</option>
                <option value="Dra.">Dra.</option>
              </select>
              <input value={previewName} onChange={e=>setPreviewName(e.target.value)}
                className="bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200" data-testid="tpl-preview-name"/>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button onClick={doPreview} disabled={previewLoading}
                className="px-3 py-2 rounded-lg border border-white/10 text-slate-200 hover:bg-white/5 text-sm flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="tpl-preview-btn">
                <Eye className="w-4 h-4"/> {previewLoading ? "Gerando…" : "Gerar prévia"}
              </button>
              <button onClick={sendTest}
                className="px-3 py-2 rounded-lg bg-slate-800 text-slate-100 hover:bg-slate-700 text-sm flex items-center justify-center gap-2"
                data-testid="tpl-preview-send">
                <Send className="w-4 h-4"/> Enviar teste para mim
              </button>
            </div>
            {previewSrc ? (
              <img src={previewSrc} alt="prévia" className="w-full rounded-lg border border-white/10" data-testid="tpl-preview-img"/>
            ) : (
              <div className="h-40 flex items-center justify-center text-sm text-slate-500 border border-dashed border-white/10 rounded-lg">
                Clique em "Gerar prévia" para visualizar.
              </div>
            )}
            <div className="mt-3 text-[11px] text-slate-500">
              Alterações no texto/imagem entram em vigor imediatamente nos próximos envios automáticos.
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function FieldTA({ label, testid, rows = 2, ...rest }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <textarea rows={rows} {...rest} data-testid={testid}
        className="w-full px-3 py-2 rounded-lg bg-[#0a0d17] border border-white/5 text-sm text-slate-200 focus:ring-2 focus:ring-[#d4a24c] outline-none resize-y"/>
    </label>
  );
}
